import { HistoryOutlined } from '@mui/icons-material'
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import { Activity, activityUserName, groupActivitiesByDay } from './activityMeta'
import { ActivityItem } from './ActivityItem'
import { PaginationWithJump } from '../common/PaginationWithJump'

interface ActivityTimelineProps {
  activities?: (Activity | null)[] | null
  loading: boolean
  error: boolean
  onRetry: () => void
  emptyTitle: string
  emptyHint: string
  /** Drop the "for (#matter) title" suffix when every activity belongs to the same matter. */
  hideMatterReference?: boolean
}

/** A page of activity, newest first, grouped by day; with its loading, error and empty states. */
export const ActivityTimeline = ({
  activities,
  loading,
  error,
  onRetry,
  emptyTitle,
  emptyHint,
  hideMatterReference
}: ActivityTimelineProps) => {
  const groups = useMemo(() => groupActivitiesByDay((activities ?? []).filter(Boolean) as Activity[]), [activities])

  if (loading && !groups.length)
    return (
      <Stack gap={2.5}>
        {Array.from({ length: 4 }, (_, index) => (
          <Stack key={index} direction="row" gap={2}>
            <Skeleton variant="circular" width={36} height={36} />
            <Box flex={1}>
              <Skeleton width="40%" />
              <Skeleton width="75%" />
              <Skeleton width="20%" />
            </Box>
          </Stack>
        ))}
      </Stack>
    )

  if (error)
    return (
      <Stack alignItems="center" gap={1.5} py={6}>
        <Typography fontSize={14} color="lightGrayFive">
          Couldn't load activity.
        </Typography>
        <Button variant="outlined" size="small" onClick={onRetry}>
          Try again
        </Button>
      </Stack>
    )

  if (!groups.length)
    return (
      <Stack alignItems="center" gap={1} py={6}>
        <HistoryOutlined sx={{ fontSize: 40, color: 'lightGrayFour' }} />
        <Typography fontSize={14} color="gray900">
          {emptyTitle}
        </Typography>
        <Typography fontSize={12} color="lightGrayFive">
          {emptyHint}
        </Typography>
      </Stack>
    )

  return (
    <Stack gap={3} sx={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
      {groups.map(({ key, label, items }) => (
        <Box key={key}>
          <Typography fontSize={12} color="lightGrayFive" textTransform="uppercase" letterSpacing={0.5} mb={1.5}>
            {label}
          </Typography>

          <Box component="ul" m={0} p={0}>
            {items.map((activity, index) => (
              <ActivityItem
                key={activity.id}
                action={activity.action}
                description={activity.description}
                entityType={activity.entityType}
                billId={activity.billId}
                hideMatterReference={hideMatterReference}
                userName={activityUserName(activity)}
                createdAt={activity.createdAt}
                isLast={index === items.length - 1}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Stack>
  )
}

interface ActivityPageFooterProps {
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
  onPageChange: (page: number) => void
}

export const ActivityPageFooter = ({
  page,
  pageSize,
  totalCount,
  totalPages,
  onPageChange
}: ActivityPageFooterProps) => {
  if (!totalCount) return null

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
      gap={2}
      px={3}
      py={2}
      borderTop="1px solid"
      borderColor="lightGrayTwo"
    >
      <Typography fontSize={12} color="lightGrayFive">
        Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount}
      </Typography>

      {totalPages > 1 && (
        <PaginationWithJump count={totalPages} page={page} onChange={onPageChange} size="small" sx={{ p: 0 }} />
      )}
    </Stack>
  )
}
